import { type Action } from '@ngrx/store';
import type { LibraryBlockApiEntity } from '@web/store/library-blocks/library-blocks.models';
import { prepareDataForStore, TempStatus } from '@web/store/library-blocks/library-blocks.models';

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { forkJoin, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { getUuid } from '@common/helpers';

import { LibraryBlocksHttpService } from '@web/store/library-blocks/library-blocks.http.service';
import { LibraryBlocksService } from '@web/store/library-blocks/library-blocks.service';

import { AppFacade } from '@web/store/app/app.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { LibraryBlockFacade } from '@web/store/library-blocks/library-blocks.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { serializeThemeSettings } from '@sites/site-theme';

import AppActions from '@web/store/app/app.actions';
import BlocksActions from '@web/store/page-structure/blocks/blocks.actions';
import SiteActions from '@web/store/website/site/site.actions';
import NotifyAction from '@common/utils/notify/notify.actions';
import * as LibraryBlocksActions from './library-blocks.actions';

@Injectable()
export class LibraryBlocksEffects {
    private readonly actions$ = inject(Actions);
    private readonly libraryBlocksService = inject(LibraryBlocksService);
    private readonly libraryBlockFacade = inject(LibraryBlockFacade);
    private readonly libraryBlockHttpService = inject(LibraryBlocksHttpService);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly themeState = inject(ThemeStateService);
    private readonly siteFacade = inject(SiteFacade);
    private readonly appFacade = inject(AppFacade);
    private readonly translateService = inject(TranslateService);

    public readonly init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LibraryBlocksActions.initLibraryBlocks),
            withLatestFrom(this.siteFacade.id$, this.appFacade.isAdmin$, (_, websiteId, isAdmin) => ({
                websiteId,
                isAdmin,
            })),
            filter(({ isAdmin }) => isAdmin),
            switchMap(({ websiteId }) =>
                this.libraryBlockHttpService
                    .getLibraryBlocks(websiteId)
                    .pipe(
                        map((libraryBlocks: LibraryBlockApiEntity[]) =>
                            LibraryBlocksActions.loadLibraryBlocksSuccess({ libraryBlocks })
                        )
                    )
            )
        )
    );

    public readonly initUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.initAppSuccess, LibraryBlocksActions.initLibraryBlocks),
            withLatestFrom(this.appFacade.isAdmin$, (_, isAdmin) => isAdmin),
            filter((isAdmin) => !isAdmin),
            switchMap(() => {
                return this.libraryBlockHttpService
                    .getUserLibraryBlocks(this.libraryBlocksService.getLanguage())
                    .pipe(
                        map((libraryBlocks: LibraryBlockApiEntity[]) =>
                            LibraryBlocksActions.loadLibraryBlocksSuccess({ libraryBlocks })
                        )
                    );
            })
        )
    );

    alreadyDeletedBlocks = [];
    public readonly proceedLibraryBlocks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SiteActions.saveSiteSuccess,
                LibraryBlocksActions.upsertLibraryBlock,
                LibraryBlocksActions.markAsDeleted
            ),
            concatLatestFrom(() => [
                this.libraryBlockFacade.allLibraryBlocks$,
                this.blocksFacade.structure$,
                this.appFacade.isAdmin$,
            ]),
            // The theme is a signal now, so it is read where it is used rather than combined in —
            // a plain read at emission time, which is exactly what the combinator was doing.
            map(([action, libraryBlocks, structure, isAdmin]) => ({
                libraryBlocks,
                structure,
                themeSettings: this.themeState.settings(),
                isAdmin,
                action,
            })),
            filter(({ isAdmin, action }) => {
                if (action.type === LibraryBlocksActions.upsertLibraryBlock.type) {
                    return !isAdmin && !action.skipCreate;
                }
                if (action.type === LibraryBlocksActions.markAsDeleted.type) {
                    return !isAdmin;
                }

                return true;
            }),
            map(({ libraryBlocks, isAdmin, structure, themeSettings, action }) => {
                if (!isAdmin) {
                    libraryBlocks = libraryBlocks.filter((libraryBlock) => !!libraryBlock.userId);
                }
                return { libraryBlocks, isAdmin, structure, themeSettings, type: action.type };
            }),
            mergeMap(({ libraryBlocks, structure, themeSettings, isAdmin, type }) => {
                const requests = libraryBlocks
                    .filter((block) => {
                        if ('id' in block && block.tempStatus !== TempStatus.new) {
                            return !this.alreadyDeletedBlocks.includes(block.id);
                        }
                        return true;
                    })
                    .map((libraryBlock) => {
                        if ([TempStatus.created, TempStatus.new].includes(libraryBlock.tempStatus)) {
                            const structureBlock = structure.find((block) => block.id === libraryBlock.blockId);
                            libraryBlock = {
                                ...libraryBlock,
                                blockId: isAdmin ? libraryBlock.blockId : getUuid(),
                                content: [
                                    {
                                        name: 'structure',
                                        value: JSON.stringify([structureBlock]),
                                    },
                                    {
                                        name: 'theme_settings',
                                        value: serializeThemeSettings(themeSettings),
                                    },
                                ],
                            };
                        }
                        if (libraryBlock.tempStatus === TempStatus.new && (!('id' in libraryBlock) || !isAdmin)) {
                            return this.libraryBlockHttpService
                                .createLibraryBlock(libraryBlock, isAdmin)
                                .pipe(map((res) => ({ ...res, createBlock: true })));
                        } else if (libraryBlock.tempStatus === TempStatus.created && 'id' in libraryBlock) {
                            return isAdmin ? this.libraryBlockHttpService.updateLibraryBlock(libraryBlock) : of(null);
                        } else if (libraryBlock.tempStatus !== TempStatus.new && 'id' in libraryBlock) {
                            this.alreadyDeletedBlocks.push(libraryBlock.id);
                            return this.libraryBlockHttpService.deleteLibraryBlock(libraryBlock, isAdmin);
                        } else {
                            return of(null);
                        }
                    });

                return forkJoin({ type: of(type), responses: forkJoin(requests) });
            }),
            switchMap(({ type, responses }) => {
                const blocks = responses.filter((res) => {
                    if (res && 'createBlock' in res) {
                        return true;
                    }
                    return false;
                });

                const actions: Action[] = [LibraryBlocksActions.initLibraryBlocks()];

                prepareDataForStore(blocks).forEach((block) => {
                    actions.push(LibraryBlocksActions.upsertLibraryBlock({ libraryBlock: block, skipCreate: true }));
                });

                if (type === LibraryBlocksActions.upsertLibraryBlock.type) {
                    actions.push(
                        NotifyAction.showNotify({
                            toastType: 'success',
                            message: this.translateService.instant('user_block_saved'),
                        })
                    );
                }
                return actions;
            })
        )
    );

    public readonly copyLibraryBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.blockCopied),
            withLatestFrom(
                this.appFacade.isAdmin$,
                this.siteFacade.id$,
                this.libraryBlockFacade.libraryBlocksEntities$,
                ({ initialBlock, copiedBlock }, isAdmin, templateId, libraryBlockEntities) => ({
                    initialBlock,
                    copiedBlock,
                    libraryBlockEntities,
                    isAdmin,
                    templateId,
                })
            ),
            filter(({ isAdmin, libraryBlockEntities, initialBlock }) =>
                Boolean(isAdmin && libraryBlockEntities[initialBlock.id])
            ),
            map(({ initialBlock, copiedBlock, libraryBlockEntities, templateId }) =>
                LibraryBlocksActions.upsertLibraryBlock({
                    libraryBlock: this.libraryBlocksService.copyBlockEntity(
                        templateId,
                        copiedBlock.id,
                        libraryBlockEntities[initialBlock.id]
                    ),
                })
            )
        )
    );
}
